import type { NextConfigComplete } from '../server/config-shared'
import type { Telemetry } from '../telemetry/storage'
import type { Span } from '../trace'

import * as Log from './output/log'
import { Worker } from '../lib/worker'
import createSpinner from './spinner'
import { eventTypeCheckCompleted } from '../telemetry/events'
import isError from '../lib/is-error'
import { hrtimeDurationToString } from './duration-to-string'
import { getBuildDistDir } from '../export/utils'

/**
 * TypeScript setup and type checking run in a worker so the compiler's memory
 * can be released before the rest of the build continues.
 *
 * Since it is impossible to pass a function from main thread to a worker,
 * instead of running "next/lib/typescript/runTypeCheck" in a worker,
 * we will run entire "next/lib/verify-typescript-setup" in a worker instead.
 */
function verifyAndRunTypeScript(
  dir: string,
  distDir: string,
  strictRouteTypes: boolean,
  shouldRunTypeCheck: boolean,
  tsconfigPath: string | undefined,
  typedRoutes: boolean,
  disableStaticImages: boolean,
  cacheDir: string | undefined,
  enableWorkerThreads: boolean | undefined,
  hasAppDir: boolean,
  hasPagesDir: boolean,
  appDir: string | undefined,
  pagesDir: string | undefined,
  debugBuildPaths: { app: string[]; pages: string[] } | undefined,
  useTypeScriptCli: boolean,
  onFirstCliOutput?: () => void
) {
  let impl: typeof import('../lib/verify-typescript-setup').verifyAndRunTypeScript
  let typeCheckWorker:
    | (Worker & {
        verifyAndRunTypeScriptInWorker: typeof impl
      })
    | undefined
  if (shouldRunTypeCheck && !useTypeScriptCli) {
    typeCheckWorker = new Worker(
      require.resolve('../lib/verify-typescript-setup'),
      {
        exposedMethods: ['verifyAndRunTypeScriptInWorker'],
        debuggerPortOffset: -1,
        isolatedMemory: false,
        numWorkers: 1,
        enableWorkerThreads,
        maxRetries: 0,
      }
    ) as typeof typeCheckWorker
    impl = typeCheckWorker!.verifyAndRunTypeScriptInWorker
  } else {
    // No worker: either we are not type-checking (just writing setup files), or
    // the CLI checker runs `tsc` in-process. Avoid the worker overhead.
    impl = (
      require('../lib/verify-typescript-setup') as typeof import('../lib/verify-typescript-setup')
    ).verifyAndRunTypeScript
  }

  return impl({
    dir,
    distDir,
    strictRouteTypes,
    shouldRunTypeCheck,
    tsconfigPath,
    typedRoutes,
    disableStaticImages,
    cacheDir,
    hasAppDir,
    hasPagesDir,
    appDir,
    pagesDir,
    debugBuildPaths,
    useTypeScriptCli,
    onFirstCliOutput,
  })
    .then((result) => {
      typeCheckWorker?.end()
      return result
    })
    .catch(() => {
      // The error is already logged (in the worker for the API checker, or
      // directly for the in-process CLI checker); we simply exit to prevent the
      // `Jest worker encountered 1 child process exceptions, exceeding retry
      // limit` message from showing up.
      process.exit(1)
    })
}

export async function startTypeChecking({
  cacheDir,
  config,
  dir,
  nextBuildSpan,
  pagesDir,
  telemetry,
  appDir,
  debugBuildPaths,
}: {
  cacheDir: string
  config: NextConfigComplete
  dir: string
  nextBuildSpan: Span
  pagesDir?: string
  telemetry: Telemetry
  appDir?: string
  debugBuildPaths: { app: string[]; pages: string[] } | undefined
}) {
  const ignoreTypeScriptErrors = Boolean(config.typescript.ignoreBuildErrors)
  const useTypeScriptCli = Boolean(config.experimental.useTypeScriptCli)

  if (ignoreTypeScriptErrors) {
    Log.info('Skipping validation of types')
  }

  let typeCheckingSpinnerPrefixText: string | undefined
  let typeCheckingSpinner: ReturnType<typeof createSpinner> | undefined

  if (!ignoreTypeScriptErrors) {
    typeCheckingSpinnerPrefixText = 'Running TypeScript'
  }

  if (typeCheckingSpinnerPrefixText) {
    typeCheckingSpinner = createSpinner(typeCheckingSpinnerPrefixText)
  }

  const typeCheckAndLintStart = process.hrtime()

  try {
    const [verifyResult, typeCheckEnd] = await nextBuildSpan
      .traceChild('run-typescript')
      .traceAsyncFn(() =>
        verifyAndRunTypeScript(
          dir,
          getBuildDistDir(config),
          Boolean(config.experimental.strictRouteTypes),
          !ignoreTypeScriptErrors,
          config.typescript.tsconfigPath,
          Boolean(config.typedRoutes),
          config.images.disableStaticImages,
          cacheDir,
          config.experimental.workerThreads,
          !!appDir,
          !!pagesDir,
          appDir,
          pagesDir,
          debugBuildPaths,
          useTypeScriptCli,
          // Stop the spinner before as soon as the subprocess reports output.
          useTypeScriptCli && typeCheckingSpinner
            ? () => typeCheckingSpinner.stop()
            : undefined
        ).then((resolved) => {
          const checkEnd = process.hrtime(typeCheckAndLintStart)
          return [resolved, checkEnd] as const
        })
      )

    if (typeCheckingSpinner) {
      typeCheckingSpinner.stop()
    }

    createSpinner(
      `Finished TypeScript${ignoreTypeScriptErrors ? ' config validation' : ''} in ${hrtimeDurationToString(typeCheckEnd)}`
    )?.stopAndPersist()

    if (!ignoreTypeScriptErrors && verifyResult) {
      telemetry.record(
        eventTypeCheckCompleted({
          durationInSeconds: typeCheckEnd[0],
          typescriptVersion: verifyResult.version,
          inputFilesCount: verifyResult.result?.inputFilesCount,
          totalFilesCount: verifyResult.result?.totalFilesCount,
          incremental: verifyResult.result?.incremental,
          typeCheckMode: verifyResult.typeCheckMode,
        })
      )
    }
  } catch (err) {
    // prevent showing jest-worker internal error as it
    // isn't helpful for users and clutters output
    if (isError(err) && err.message === 'Call retries were exceeded') {
      await telemetry.flush()
      process.exit(1)
    }
    throw err
  }
}
